/**
 * Main container component for the CASCADES application
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDetentCache } from '../hooks/useDetentCache';
import { useUrlSync } from '../hooks/useUrlSync';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { CascadeStack } from './CascadeStack';
import { DetentControl } from './DetentControl';
import { SettingsModal } from './SettingsModal';
import styles from './CascadeViewer.module.css';
import type { DetentData } from '../types';

export const CascadeViewer: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { state, updateDetent, updatePosition } = useUrlSync();
  const { fetchDetent, getDetent, isLoading, getError } = useDetentCache();
  const { isDarkMode } = useTheme();
  const { settings } = useSettings();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [allDetents, setAllDetents] = useState<string[]>([]);
  // Keep track of last successfully loaded detent to show while loading new one
  const lastLoadedDetentRef = useRef<DetentData | null>(null);

  // Fetch detents list on mount
  useEffect(() => {
    const fetchDetentsList = async () => {
      try {
        const response = await fetch('/data/detents.json');
        const data = await response.json();
        const years = data.years?.map((y: number) => y.toString()) || [];
        const zany = data.zany?.map((z: { id: string }) => z.id) || [];
        setAllDetents([...years, ...zany]);
      } catch (error) {
        console.error('Failed to fetch detents list:', error);
      }
    };
    fetchDetentsList();
  }, []);

  // Fetch detent data when detentId changes
  useEffect(() => {
    if (state.detentId) {
      fetchDetent(state.detentId);
    }
  }, [state.detentId, fetchDetent]);

  // Preload adjacent detents for smooth swiping
  useEffect(() => {
    if (!state.detentId || allDetents.length === 0) return;

    const currentIndex = allDetents.indexOf(state.detentId);
    if (currentIndex === -1) return;

    // Preload previous and next detents in the background
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : allDetents.length - 1;
    const nextIndex = currentIndex < allDetents.length - 1 ? currentIndex + 1 : 0;

    // Delay preloading slightly to prioritize current detent
    const timer = setTimeout(() => {
      fetchDetent(allDetents[prevIndex]);
      fetchDetent(allDetents[nextIndex]);
    }, 100);

    return () => clearTimeout(timer);
  }, [state.detentId, allDetents, fetchDetent]);

  const currentDetent = state.detentId ? getDetent(state.detentId) : null;
  const loading = state.detentId ? isLoading(state.detentId) : false;
  const error = state.detentId ? getError(state.detentId) : null;

  // Update last loaded detent when we have new data
  if (currentDetent) {
    lastLoadedDetentRef.current = currentDetent;
  }

  // Use current detent if available, otherwise fall back to last loaded
  // This prevents blank screen during loading transitions
  const displayDetent = currentDetent || lastLoadedDetentRef.current;

  // Get active block for title
  const activeBlock = displayDetent?.blocks?.[0] || null;

  // Get the detent label from either the active block's label or the detent's ID/year
  const getDetentLabel = () => {
    if (!displayDetent) return null;

    // If it's a zany detent, use the active block's label or the detent's ID
    if (displayDetent.isZany) {
      return activeBlock?.label || displayDetent.id;
    }

    // For year detents, use the year
    return displayDetent.year.toString();
  };

  const descriptiveText = displayDetent
    ? displayDetent.isZany
      ? `${getDetentLabel()} in X words`
      : `describe ${getDetentLabel()} in X words`
    : null;

  const handleRandomize = useCallback(() => {
    if (allDetents.length === 0) return;
    const pool = allDetents.length > 1
      ? allDetents.filter((id) => id !== state.detentId)
      : allDetents;
    const target = pool[Math.floor(Math.random() * pool.length)];
    if (target) {
      updateDetent(target);
    }
  }, [allDetents, state.detentId, updateDetent]);

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light} ${settings.portraitMode ? styles.portrait : ''}`} ref={containerRef}>
      <header className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <button
              type="button"
              className={styles.logoButton}
              onClick={handleRandomize}
              aria-label="Random detent"
              title="Jump to a random detent"
            >
              <img src="/favicon.png" alt="" className={styles.logo} />
            </button>
            {descriptiveText || 'CASCADES'}
          </h1>
        </div>
        <div className={styles.controls}>
          <button
            className={styles.settingsButton}
            onClick={() => setIsSettingsOpen(true)}
            aria-label="Settings"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M17.43 10.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C12.46 0 12.24 0 12 0h-4c-.24 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.22-.08-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.24 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM10 13c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z"/>
            </svg>
          </button>
        </div>
      </header>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentDetentId={state.detentId}
      />

      <DetentControl
        selectedDetentId={state.detentId}
        onDetentChange={updateDetent}
      />

      <main className={styles.main}>
        {error && (
          <div className={styles.error}>
            Error loading cascade: {error}
          </div>
        )}

        {displayDetent && !error && (
          <CascadeStack
            detent={displayDetent}
            lensId={state.lensId}
            position={state.position}
            onPositionChange={updatePosition}
            onDetentChange={updateDetent}
          />
        )}

        {!state.detentId && !loading && !displayDetent && (
          <div className={styles.placeholder}>
            Select a detent to view cascade data
          </div>
        )}
      </main>
    </div>
  );
};
